import { useEffect, useState } from "react";
import axios from "axios";
import { Users, MousePointerClick, Timer, LogOut as Bounce, Megaphone, CircleAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const num = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("pt-PT", { maximumFractionDigits: 2 }));
const chf = (v) => (v === null || v === undefined ? "—" : `CHF ${Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v === null || v === undefined ? "—" : `${(Number(v) * 100).toLocaleString("pt-PT", { maximumFractionDigits: 1 })}%`);
const dur = (s) => {
    if (s === null || s === undefined) return "—";
    const m = Math.floor(s / 60);
    return `${m}m ${Math.round(s % 60)}s`;
};

const Card = ({ rotulo, valor, sub, testid }) => (
    <div className="border border-[#c9a24b]/30 bg-[#0a0705] p-5" data-testid={testid}>
        <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/60">{rotulo}</p>
        <p className="font-display gold-text text-2xl sm:text-3xl mt-2">{valor}</p>
        {sub && <p className="font-light text-[11px] text-[#efe6d6]/45 mt-1">{sub}</p>}
    </div>
);

const PorConfigurar = ({ titulo, testid, children }) => (
    <div className="border border-dashed border-[#c9a24b]/25 p-6 mt-6" data-testid={testid}>
        <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] flex items-center gap-2">
            <CircleAlert size={14} /> {titulo} — por configurar
        </p>
        <div className="font-light text-sm text-[#efe6d6]/60 mt-3 space-y-1.5">{children}</div>
    </div>
);

export const AdminMetrics = ({ token }) => {
    const [m, setM] = useState(null);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/admin/metrics`, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => setM(data))
            .catch(() => setErro("Não foi possível carregar as métricas."));
    }, [token]);

    if (erro) return <p className="font-display italic text-[#e0a0a0] mt-12" data-testid="metrics-erro">{erro}</p>;
    if (!m) return <p className="font-display italic text-[#efe6d6]/60 mt-12" data-testid="metrics-loading">A carregar métricas...</p>;

    const ga4 = m.ga4;
    const meta = m.meta;
    const f = m.funil;
    const taxaConv = f.pageviews > 0 ? f.checkout_iniciado / f.pageviews : null;

    return (
        <div className="mt-10 space-y-14" data-testid="admin-metrics">
            {/* FUNIL DO SITE — contadores internos, sempre disponíveis */}
            <section data-testid="metrics-funil">
                <h2 className="font-caps text-xs tracking-[0.3em] uppercase text-[#d19a3f]">Funil do Site · últimos 28 dias</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                    <Card rotulo="Visitas (PageViews)" valor={num(f.pageviews)} testid="funil-pageviews" />
                    <Card rotulo="Checkout Iniciado" valor={num(f.checkout_iniciado)} sub="cliques para a Ticketcorner" testid="funil-checkout" />
                    <Card rotulo="Leads (Confirmações)" valor={num(f.leads)} testid="funil-leads" />
                    <Card rotulo="Contactos" valor={num(f.contactos)} testid="funil-contactos" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <Card rotulo="Taxa de Conversão" valor={pct(taxaConv)} sub="visita → checkout" testid="funil-conversao" />
                    <Card rotulo="Checkout Concluído" valor="—" sub="a compra acontece na Ticketcorner/Eventim — visível no respetivo painel" testid="funil-concluido" />
                </div>
            </section>

            {/* WEBSITE — GA4 */}
            <section data-testid="metrics-website">
                <h2 className="font-caps text-xs tracking-[0.3em] uppercase text-[#d19a3f] flex items-center gap-2">
                    <Users size={14} /> Website · Google Analytics 4
                </h2>
                {ga4 ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                            <Card rotulo="Visitantes Únicos" valor={num(ga4.visitantes_unicos)} testid="ga4-visitantes" />
                            <Card rotulo="Sessões" valor={num(ga4.sessoes)} testid="ga4-sessoes" />
                            <Card rotulo="Tempo Médio" valor={dur(ga4.tempo_medio_s)} testid="ga4-tempo" />
                            <Card rotulo="Taxa de Rejeição" valor={pct(ga4.taxa_rejeicao)} testid="ga4-rejeicao" />
                        </div>
                        <div className="grid lg:grid-cols-2 gap-6 mt-6">
                            <div className="border border-[#c9a24b]/25 overflow-x-auto" data-testid="ga4-origens">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-[#c9a24b]/25 bg-[#0a0705]">
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Origem do Tráfego</th>
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Sessões</th>
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Utilizadores</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ga4.origens.map((o) => (
                                            <tr key={o.origem} className="border-b border-[#c9a24b]/10">
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/85">{o.origem}</td>
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/60">{num(o.sessoes)}</td>
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/60">{num(o.utilizadores)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="border border-[#c9a24b]/25 overflow-x-auto" data-testid="ga4-paginas">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-[#c9a24b]/25 bg-[#0a0705]">
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Página</th>
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Visualizações</th>
                                            <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-3.5">Utilizadores</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ga4.paginas.map((p) => (
                                            <tr key={p.pagina} className="border-b border-[#c9a24b]/10">
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/85">{p.pagina}</td>
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/60">{num(p.visualizacoes)}</td>
                                                <td className="px-5 py-3 font-light text-sm text-[#efe6d6]/60">{num(p.utilizadores)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                    <PorConfigurar titulo="Google Analytics 4" testid="ga4-por-configurar">
                        <p>1. Google Cloud → ativar «Google Analytics Data API» e criar uma conta de serviço (chave JSON).</p>
                        <p>2. GA4 → Administração → Gestão de acessos da propriedade → adicionar o email da conta de serviço como Leitor.</p>
                        <p>3. Enviar-nos o ID numérico da propriedade GA4 e o JSON da conta de serviço.</p>
                    </PorConfigurar>
                )}
            </section>

            {/* META ADS */}
            <section data-testid="metrics-meta">
                <h2 className="font-caps text-xs tracking-[0.3em] uppercase text-[#d19a3f] flex items-center gap-2">
                    <Megaphone size={14} /> Meta Ads {meta && <span className="text-[#efe6d6]/40 normal-case tracking-normal font-light">· {meta.janela}</span>}
                </h2>
                {meta ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                            <Card rotulo="Impressões" valor={num(meta.totais.impressoes)} testid="meta-impressoes" />
                            <Card rotulo="Alcance" valor={num(meta.totais.alcance)} testid="meta-alcance" />
                            <Card rotulo="Gasto" valor={chf(meta.totais.gasto)} testid="meta-gasto" />
                            <Card rotulo="Conversões" valor={num(meta.totais.conversoes)} testid="meta-conversoes" />
                            <Card rotulo="Receita" valor={chf(meta.totais.receita)} sub="compras atribuídas pela Meta" testid="meta-receita" />
                            <Card rotulo="CPA" valor={chf(meta.totais.cpa)} testid="meta-cpa" />
                            <Card rotulo="ROAS" valor={meta.totais.roas === null ? "—" : `${num(meta.totais.roas)}×`} testid="meta-roas" />
                            <Card rotulo="Campanhas Ativas" valor={num(meta.campanhas.length)} testid="meta-campanhas-n" />
                        </div>
                        <div className="border border-[#c9a24b]/25 overflow-x-auto mt-6" data-testid="meta-campanhas">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-[#c9a24b]/25 bg-[#0a0705]">
                                        {["Campanha", "Impressões", "Alcance", "CTR", "CPC", "CPM", "Gasto", "Conv.", "Receita", "CPA", "ROAS"].map((h) => (
                                            <th key={h} className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-4 py-3.5 whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {meta.campanhas.map((c) => (
                                        <tr key={c.campanha} className="border-b border-[#c9a24b]/10">
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/85 whitespace-nowrap">{c.campanha}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{num(c.impressoes)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{num(c.alcance)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{pct(c.ctr / 100)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{chf(c.cpc)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{chf(c.cpm)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{chf(c.gasto)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{num(c.conversoes)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{chf(c.receita)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{chf(c.cpa)}</td>
                                            <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{c.roas === null ? "—" : `${num(c.roas)}×`}</td>
                                        </tr>
                                    ))}
                                    {meta.campanhas.length === 0 && (
                                        <tr><td colSpan={11} className="px-4 py-8 text-center font-display italic text-[#efe6d6]/50">Sem campanhas ativas no período.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <PorConfigurar titulo="Meta Ads" testid="meta-por-configurar">
                        <p>1. Meta Business Suite → Definições de Empresa → Utilizadores de Sistema → criar token com acesso de leitura à conta de anúncios.</p>
                        <p>2. Enviar-nos o token e o ID da conta de anúncios (act_…, visível no Gestor de Anúncios).</p>
                        <p>3. Para o Pixel + Conversions API: ID do Pixel e token CAPI (Gestor de Eventos → definições do Pixel).</p>
                    </PorConfigurar>
                )}
            </section>

            <p className="font-light text-[11px] text-[#efe6d6]/40 flex items-center gap-2" data-testid="metrics-nota">
                <Timer size={12} /> Cache de 30 minutos. GA4 e Meta podem não coincidir — janelas de atribuição diferentes.
            </p>
        </div>
    );
};
